import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Agent, AgentService } from '../../services/agentService';
import { FilterOption } from '../../types/common';

interface AgentsState {
  agents: Agent[];
  filterOptions: FilterOption[];
  activeFilters: string[];
  searchQuery: string;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const activeFilters = localStorage.getItem('hcltech_agents_filters');
    const searchQuery = localStorage.getItem('hcltech_agents_search');
    
    return {
      activeFilters: activeFilters ? JSON.parse(activeFilters) : [],
      searchQuery: searchQuery || '',
    };
  } catch (error) {
    console.error('Error loading agents state from localStorage:', error);
    return {
      activeFilters: [],
      searchQuery: '',
    };
  }
};

const persistedState = loadPersistedState();

const initialState: AgentsState = {
  agents: [],
  filterOptions: [],
  activeFilters: persistedState.activeFilters,
  searchQuery: persistedState.searchQuery,
  loading: false,
  error: null,
  lastFetched: null,
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Async thunk to fetch all agents
export const fetchAllAgents = createAsyncThunk(
  'agents/fetchAll',
  async (_, { getState }) => {
    const state = getState() as { agents: AgentsState };
    const now = Date.now();
    
    // Check if cache is still valid
    if (
      state.agents.lastFetched &&
      now - state.agents.lastFetched < CACHE_DURATION &&
      state.agents.agents.length > 0
    ) {
      // Return cached data
      return {
        agents: state.agents.agents,
        filterOptions: state.agents.filterOptions,
      };
    }
    
    // Fetch fresh data
    const agents = await AgentService.getAllAgents();
    const filterOptions = AgentService.getFilterOptions(agents);
    
    return { agents, filterOptions };
  }
);

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    toggleFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload;
      if (state.activeFilters.includes(filterId)) {
        state.activeFilters = state.activeFilters.filter(id => id !== filterId);
      } else {
        state.activeFilters.push(filterId);
      }
    },
    clearFilters: (state) => {
      state.activeFilters = [];
      state.searchQuery = '';
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearAgentsCache: (state) => {
      state.agents = [];
      state.filterOptions = [];
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = action.payload.agents;
        state.filterOptions = action.payload.filterOptions;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchAllAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load agents';
      });
  },
});

export const {
  toggleFilter,
  clearFilters,
  setSearchQuery,
  clearAgentsCache,
} = agentsSlice.actions;

export default agentsSlice.reducer;
